<template>
  <CCardBody>
    <CCard class="filters" accent-color="warning">
      <CCardHeader
        role="button"
        class="text-warning shadow-none card-header"
        @click="resetFilters"
      >
        <strong class="m-0"><CIcon :name="`cil-filter${$store.state.filterShow ? '-x' : ''}`" /> Filters</strong>
        <div class="card-header-actions">
          <CIcon :name="`cil-chevron-${$store.state.filterShow ? 'bottom' : 'top'}`"/>
        </div>
      </CCardHeader>

      <CCollapse :show="$store.state.filterShow">
        <CCardBody class="p-2">
          <div class="row">
            <div class="col-lg col-filter mb-2 mb-lg-0">
              <v-select
                class="v-select-filter"
                placeholder="Filter user devices by..."
                v-model="filterBy"
                :options="filterOptions"
                :reduce="option => option.value"
                multiple
                :searchable="false"
                @input="filterDevices"
              />
            </div>

            <div class="col-lg col-filter mb-2 mb-lg-0">
              <v-select
                class="v-select-filter"
                placeholder="Filter by app..."
                v-model="appFilter"
                :options="pushApps"
                :reduce="app => app.id"
                label="label"
                multiple
                :disabled="pushAppsLoading"
                @input="filterApps"
              />
            </div>

            <div class="col-lg col-filter mb-2 mb-lg-0">
              <v-select
                class="v-select-filter"
                placeholder="Platform..."
                v-model="platformFilter"
                :options="platformOptions"
                :reduce="option => option.value"
                multiple
                :searchable="false"
                @input="filterPlatforms"
              />
            </div>

            <div class="col-lg col-filter mb-2 mb-lg-0">
              <v-select
                class="v-select-filter"
                placeholder="Notify..."
                v-model="notifyFilter"
                :options="notifyOptions"
                :reduce="option => option.value"
                :searchable="false"
                @input="filterNotify"
              />
            </div>

            <div class="col-lg col-filter mb-2 mb-lg-0">
              <CInput
                class="mb-0"
                type="search"
                placeholder="Search by user, app or device..."
                v-model="search"
                @input="searchFilter"
              />
            </div>
          </div>
        </CCardBody>
      </CCollapse>
    </CCard>

    <CDataTable
      striped
      hover
      :items="loadedItems"
      :fields="fields"
      :sorter="{ external: true, resetable: true }"
      :itemsPerPageSelect="{
        external: true,
        values: [5, 15, 25, 50, 100, 250, 500],
      }"
      :items-per-page.sync="itemsPerPage"
      :loading="loading"
      :noItemsView="{
        noResults: 'No filtering results are available!',
        noItems: 'No user devices found!',
      }"
      @update:sorter-value="sorterValue"
      @pagination-change="paginationChange"
    >
      <template #show_details="{item}">
        <td class="text-center">
          <CButton size="sm" color="link" @click.stop="toggleDetails(item)">
            <CIcon :name="Boolean(item._toggled) ? 'cil-chevron-circle-up-alt' : 'cil-chevron-circle-down-alt'" />
          </CButton>
        </td>
      </template>

      <template #users_count="{item}">
        <td class="text-center">
          <CBadge :color="item.users_count > 1 ? 'warning' : 'primary'">
            {{ item.users_count }}
          </CBadge>
        </td>
      </template>

      <template #app="{item}">
        <td>{{ formatApp(item.app) }}</td>
      </template>

      <template #device_id="{item}">
        <td>{{ maskedDeviceId(item.device_id) }}</td>
      </template>

      <template #is_notify="{item}">
        <td class="text-center">
          <CBadge :color="item.is_notify ? 'success' : 'secondary'">
            {{ item.is_notify ? 'On' : 'Off' }}
          </CBadge>
        </td>
      </template>

      <template #last_active="{item}">
        <td>{{ item.last_active || item.updated_at || '-' }}</td>
      </template>
      <template #details="{item}">
        <CCollapse :show="Boolean(item._toggled)" :duration="collapseDuration">
          <div class="user-device-details">
            <div class="user-device-detail-block linked-users">
              <h6>Linked users</h6>

              <div v-if="item._users_loading" class="text-muted d-flex align-items-center">
                <CSpinner size="sm" color="primary" class="mr-2" />
                Loading users...
              </div>

              <CAlert v-else-if="item._users_error" color="danger" show class="mb-0">
                {{ item._users_error }}
              </CAlert>

              <div v-else-if="item._users.length" class="linked-users-list">
                <div v-for="user in item._users" :key="user.id" class="linked-user-row">
                  <CLink :to="{ name: 'User', params: { id: user.id }}">
                    <strong>#{{ user.id }}</strong> {{ user.first_name }} {{ user.last_name }}
                  </CLink>
                  <div class="text-muted">{{ user.email }}</div>
                </div>
              </div>

              <div v-else class="text-muted">No linked users found.</div>
            </div>

            <div class="user-device-detail-block crm-messages">
              <h6>CRM messages</h6>
              <CrmRecipientMessages
                v-if="Boolean(item._toggled)"
                :url="deviceCrmMessagesUrl(item)"
                :items-per-page-default="5"
                show-recipient
              />
            </div>
          </div>
        </CCollapse>
      </template>
    </CDataTable>

    <CCard class="actions sticky-bottom">
      <CCardBody class="p-2">
        <div class="d-flex flex-wrap align-items-center" style="gap: 0.75rem">
          <div>
            <h5 class="mt-1">Total: <span class="d-inline count bg-primary pb-1">{{ total }}</span></h5>
          </div>

          <div v-if="pages > 1" class="ml-auto">
            <CPagination
              align="center"
              :dots='false'
              :pages="pages"
              :active-page.sync="activePage"
            />
          </div>
        </div>
      </CCardBody>
    </CCard>
  </CCardBody>
</template>

<script>
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import _debounce from 'lodash/debounce'
import CrmRecipientMessages from '@/components/CrmRecipientMessages'

export default {
  name: "UserDevicesBackendTable",
  components: {
    vSelect,
    CrmRecipientMessages,
  },
  data() {
    return {
      loadedItems: [],
      fields: [
        { key: "show_details", label: "", sorter: false, _style: "min-width: 56px; width: 56px; text-align: center;" },
        { key: "id", label: "#ID", _style: "min-width: 90px;" },
        { key: "users_count", label: "Users", _style: "min-width: 110px; text-align: center;" },
        { key: "app", label: "App", sorter: false, _style: "min-width: 180px;" },
        { key: "device_model", label: "Model", _style: "min-width: 160px;" },
        { key: "device_type", label: "Type", _style: "min-width: 110px;" },
        { key: "device_os", label: "OS", _style: "min-width: 120px;" },
        { key: "manufacturer", label: "Manufacturer", _style: "min-width: 150px;" },
        { key: "device_id", label: "Device ID", sorter: false, _style: "min-width: 160px;" },
        { key: "is_notify", label: "Notify", _style: "min-width: 100px; text-align: center;" },
        { key: "last_active", label: "Last Active", _style: "min-width: 160px;" },
        { key: "location", label: "Location", _style: "min-width: 140px;" },
        { key: "timezone", label: "Timezone", _style: "min-width: 160px;" },
      ],
      filterOptions: [
        { value: 'multiple-users', label: 'Multiple Users' },
      ],
      platformOptions: [
        { value: 'android', label: 'Android' },
        { value: 'ios', label: 'iOS' },
      ],
      notifyOptions: [
        { value: 1, label: 'Notify On' },
        { value: 0, label: 'Notify Off' },
      ],
      activePage: 1,
      pages: 1,
      total: 0,
      items_per_page: null,
      orderBy: null,
      asc: null,
      search: null,
      loading: false,
      filterBy: [],
      appFilter: [],
      platformFilter: [],
      notifyFilter: null,
      pushApps: [],
      pushAppsLoading: false,
      collapseDuration: 300,
    };
  },

  beforeMount() {
    this.searchFilter = _debounce(() => {
      this.getAllItems();
    }, 1000);
  },

  async mounted() {
    this.activePage = this.queryPage;
    this.getPushApps();
    await this.getAllItems();
  },

  watch: {
    reloadParams() {
      if(this.queryPage != this.activePage)
        this.$router.push({ name: this.currentRouteName, query: { page: this.activePage } });

      this.onTableChange();
    },
  },

  computed: {
    itemsPerPage: {
      get: function () { return this.items_per_page ? this.items_per_page : parseInt(process.env.VUE_APP_ITEMS_PER_PAGE) },
      set: function (newValue) { this.items_per_page = newValue }
    },
    reloadParams() { return [this.activePage]; },
    queryPage() { return parseInt(this.$route.query.page) || 1; },
    currentRouteName () { return this.$route.name },
  },

  methods: {
    async getAllItems() {
      this.loading = true;

      const url = new URL(this.$backend.USER_DEVICES.GET_ALL)
      const params = {
        page: this.activePage,
        itemsPerPage: this.itemsPerPage,
        search: this.search,
        orderBy: this.orderBy,
        asc: this.asc,
        filter: this.filterBy,
        app_id: this.appFilter,
        platform: this.platformFilter,
        notify: this.notifyFilter,
      };

      Object.keys(params).forEach((key) => {
        if (
          typeof params[key] !== "undefined" &&
          params[key] !== null &&
          params[key] !== "" &&
          params[key].length !== 0
        ) {
          url.searchParams.append(key, params[key]);
        }
      });

      await this.$axios
        .get(url)
        .then((response) => response.data)
        .then((items) => {
          if(items.current_page > items.last_page) {
            this.activePage = 1;
          } else {
            this.loadedItems = items.data.map((item) => ({
              ...item,
              _classes: item.users_count > 1 ? 'table-warning' : '',
              _users: [],
              _users_loaded: false,
              _users_loading: false,
              _users_error: null,
            }));
            this.activePage = items.current_page;
            this.pages = items.last_page;
            this.total = items.total;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onTableChange() {
      this.getAllItems();
    },
    paginationChange(value) {
      this.itemsPerPage = value;
      this.getAllItems();
    },
    sorterValue(item) {
      this.orderBy = item.column;
      this.asc = item.column ? item.asc : null;
      this.getAllItems();
    },
    filterDevices(value) {
      this.filterBy = value;
      this.getAllItems();
    },
    filterApps(value) {
      this.appFilter = value;
      this.getAllItems();
    },
    filterPlatforms(value) {
      this.platformFilter = value;
      this.getAllItems();
    },
    filterNotify(value) {
      this.notifyFilter = value;
      this.getAllItems();
    },
    resetFilters() {
      if (this.$store.state.filterShow) {
        this.search = "";
        this.filterBy = [];
        this.appFilter = [];
        this.platformFilter = [];
        this.notifyFilter = null;
        this.getAllItems();
      }
      this.$store.commit('toggle', 'filterShow');
    },
    async getPushApps() {
      if (this.pushApps.length || this.pushAppsLoading) {
        return;
      }

      this.pushAppsLoading = true;

      try {
        const response = await this.$axios.get(this.$backend.PUSHER.APP_LIST);
        this.pushApps = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Apps could not be loaded.';

        this.$toast.error(message);
      } finally {
        this.pushAppsLoading = false;
      }
    },
    maskedDeviceId(deviceId) {
      if (!deviceId) {
        return '-';
      }

      if (deviceId.length <= 8) {
        return deviceId;
      }

      return `${deviceId.slice(0, 4)}...${deviceId.slice(-4)}`;
    },
    formatApp(app) {
      if (!app) {
        return '-';
      }

      return [app.provider, app.domain || app.app_id]
        .filter(Boolean)
        .join(' - ');
    },
    toggleDetails(item) {
      const shouldOpen = !item._toggled;

      this.$set(item, '_toggled', shouldOpen);

      if (shouldOpen) {
        this.loadDeviceUsers(item);
      }
    },
    async loadDeviceUsers(item) {
      if (item._users_loaded || item._users_loading) {
        return;
      }

      this.$set(item, '_users_loading', true);
      this.$set(item, '_users_error', null);

      try {
        const response = await this.$axios.get(this.deviceUsersUrl(item));
        const users = Array.isArray(response.data.data) ? response.data.data : [];

        this.$set(item, '_users', users);
        this.$set(item, '_users_loaded', true);
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Linked users could not be loaded.';

        this.$set(item, '_users_error', message);
      } finally {
        this.$set(item, '_users_loading', false);
      }
    },
    deviceUsersUrl(item) {
      return this.$backend.USER_DEVICES.USERS.replace('{id}', item.id);
    },
    deviceCrmMessagesUrl(item) {
      return this.$backend.USER_DEVICES.CRM_MESSAGES.replace('{id}', item.id);
    },
  },
};
</script>

<style scoped>
.user-device-details {
  padding: 1rem;
  border-top: 1px solid #d8dbe0;
  background-color: #f8f9fa;
  display: grid;
  grid-template-columns: minmax(220px, 320px) minmax(0, 1fr);
  gap: 1rem;
}

.user-device-detail-block h6 {
  font-size: 0.875rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
}

.linked-users-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.linked-user-row {
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #d8dbe0;
}

.linked-user-row:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

@media (max-width: 991.98px) {
  .user-device-details {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
